import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import type { SsoProtocol } from '../../types/database'

interface SsoApp {
  id: string
  name: string
  description: string | null
  image_url: string | null
}
import {
  VStack,
  HStack,
  Card,
  PageHeader,
  Text,
  Button,
  IconButton,
  Input,
  Textarea,
  Switch,
  Select,
  Badge,
  Banner,
  Animate,
  Tabs,
  TabPanels,
  TabPanel,
  ConfirmDialog,
  CopyField,
  Grid
} from 'wss3-forge'
import {
  ArrowLeft20Regular,
  Save20Regular,
  Delete20Regular,
  Eye20Regular,
  EyeOff20Regular,
  Key20Regular,
  Shield20Regular,
  Settings20Regular
} from '@fluentui/react-icons'

export function OrgSsoForm() {
  const navigate = useNavigate()
  const { connectionId } = useParams()
  const { organization } = useOrg()
  const isEdit = connectionId !== 'new' && Boolean(connectionId)

  const [applications, setApplications] = useState<SsoApp[]>([])
  const [protocol, setProtocol] = useState<SsoProtocol>('saml')
  const [appId, setAppId] = useState('')
  const [isActive, setIsActive] = useState(true)

  // SAML config
  const [samlIdpEntityId, setSamlIdpEntityId] = useState('')
  const [samlIdpSsoUrl, setSamlIdpSsoUrl] = useState('')
  const [samlIdpCertificate, setSamlIdpCertificate] = useState('')

  // OIDC config
  const [oidcIssuer, setOidcIssuer] = useState('')
  const [oidcClientId, setOidcClientId] = useState('')
  const [oidcClientSecret, setOidcClientSecret] = useState('')
  const [oidcAuthorizationUrl, setOidcAuthorizationUrl] = useState('')
  const [oidcTokenUrl, setOidcTokenUrl] = useState('')
  const [oidcUserinfoUrl, setOidcUserinfoUrl] = useState('')

  // Options
  const [jitProvisioning, setJitProvisioning] = useState(true)
  const [autoUpdateProfile, setAutoUpdateProfile] = useState(true)
  const [defaultRole, setDefaultRole] = useState('user')

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState('config')
  const [showSecret, setShowSecret] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    loadApplications()
    if (isEdit && connectionId) {
      loadConnection()
    } else {
      setLoading(false)
    }
  }, [isEdit, connectionId])

  const loadApplications = async () => {
    const { data } = await supabase.rpc('get_org_sso_apps', {
      p_organization_id: organization.id
    })

    setApplications(data || [])
  }

  const loadConnection = async () => {
    try {
      const { data, error } = await supabase
        .from('sso_connections')
        .select('*')
        .eq('id', connectionId)
        .eq('organization_id', organization.id)
        .single()

      if (error) throw error

      if (data) {
        setProtocol(data.protocol)
        setAppId(data.app_id)
        setIsActive(data.is_active)
        setSamlIdpEntityId(data.saml_idp_entity_id || '')
        setSamlIdpSsoUrl(data.saml_idp_sso_url || '')
        setSamlIdpCertificate(data.saml_idp_certificate || '')
        setOidcIssuer(data.oidc_issuer || '')
        setOidcClientId(data.oidc_client_id || '')
        setOidcClientSecret(data.oidc_client_secret || '')
        setOidcAuthorizationUrl(data.oidc_authorization_url || '')
        setOidcTokenUrl(data.oidc_token_url || '')
        setOidcUserinfoUrl(data.oidc_userinfo_url || '')
        setJitProvisioning(data.jit_provisioning)
        setAutoUpdateProfile(data.auto_update_profile)
        setDefaultRole(data.default_role || 'user')
      }
    } catch {
      setErrors({ general: 'Error loading connection' })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {}

    if (!appId && !isEdit) {
      newErrors.appId = 'Select an application'
    }

    if (protocol === 'saml') {
      if (!samlIdpEntityId.trim()) newErrors.samlIdpEntityId = 'Entity ID is required'
      if (!samlIdpSsoUrl.trim()) newErrors.samlIdpSsoUrl = 'SSO URL is required'
      if (!samlIdpCertificate.trim()) newErrors.samlIdpCertificate = 'Certificate is required'
    } else {
      if (!oidcClientId.trim()) newErrors.oidcClientId = 'Client ID is required'
      if (!oidcClientSecret.trim()) newErrors.oidcClientSecret = 'Client Secret is required'
      if (!oidcAuthorizationUrl.trim()) newErrors.oidcAuthorizationUrl = 'Authorization URL is required'
      if (!oidcTokenUrl.trim()) newErrors.oidcTokenUrl = 'Token URL is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setSaving(true)

    const data = {
      protocol,
      is_active: isActive,
      saml_idp_entity_id: protocol === 'saml' ? samlIdpEntityId : null,
      saml_idp_sso_url: protocol === 'saml' ? samlIdpSsoUrl : null,
      saml_idp_certificate: protocol === 'saml' ? samlIdpCertificate : null,
      oidc_issuer: protocol === 'oidc' ? oidcIssuer : null,
      oidc_client_id: protocol === 'oidc' ? oidcClientId : null,
      oidc_client_secret: protocol === 'oidc' ? oidcClientSecret : null,
      oidc_authorization_url: protocol === 'oidc' ? oidcAuthorizationUrl : null,
      oidc_token_url: protocol === 'oidc' ? oidcTokenUrl : null,
      oidc_userinfo_url: protocol === 'oidc' ? oidcUserinfoUrl : null,
      jit_provisioning: jitProvisioning,
      auto_update_profile: autoUpdateProfile,
      default_role: defaultRole
    }

    try {
      if (isEdit) {
        const { error } = await supabase
          .from('sso_connections')
          .update(data)
          .eq('id', connectionId)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('sso_connections')
          .insert({
            ...data,
            organization_id: organization.id,
            app_id: appId === 'all' ? null : appId
          })

        if (error) throw error
      }

      navigate(`/org/${organization.slug}/sso`)
    } catch (err: any) {
      setErrors({ general: err.message })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('sso_connections')
        .delete()
        .eq('id', connectionId)

      if (error) throw error
      navigate(`/org/${organization.slug}/sso`)
    } catch (err: any) {
      setErrors({ general: err.message })
    }
    setShowDeleteConfirm(false)
  }

  const spEntityId = `https://account.webba-creative.com/saml/${organization.slug}`
  const acsUrl = `https://account.webba-creative.com/auth/sso/callback`
  const oidcRedirectUri = `https://account.webba-creative.com/auth/sso/callback`

  if (loading) {
    return <Text>Loading...</Text>
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <VStack gap="md">
          <Button
            variant="ghost"
            icon={<ArrowLeft20Regular />}
            onClick={() => navigate(`/org/${organization.slug}/sso`)}
          >
            Back to SSO
          </Button>
          <PageHeader
            title={isEdit ? 'Edit Connection' : 'New SSO Connection'}
            subtitle={isEdit ? 'Modify the configuration' : 'Configure SAML or OIDC'}
            actions={
              isEdit ? (
                <Button
                  variant="danger"
                  icon={<Delete20Regular />}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </Button>
              ) : undefined
            }
          />
        </VStack>
      </Animate>

      {errors.general && (
        <Banner variant="error" title="Error">{errors.general}</Banner>
      )}

      <Animate type="slideInUp" delay={50}>
        <Tabs
          tabs={[
            { id: 'config', label: 'Configuration', icon: <Key20Regular /> },
            { id: 'options', label: 'Options', icon: <Settings20Regular /> },
            ...(isEdit ? [{ id: 'info', label: 'SP Info', icon: <Shield20Regular /> }] : [])
          ]}
          active={activeTab}
          onChange={setActiveTab}
          stretchLine
        />

        <div style={{ marginTop: '1.5rem' }}>
        <TabPanels active={activeTab}>
          <TabPanel id="config" active={activeTab}>
            <VStack gap="lg">
              {!isEdit && (
                <Card padding="lg" title="Application">
                  <Select
                    label="Select an application"
                    value={appId}
                    onChange={(v) => setAppId(v as string)}
                    options={[
                      { value: 'all', label: 'All Applications (Platform-wide)' },
                      ...applications.map(app => ({
                        value: app.id,
                        label: app.name
                      }))
                    ]}
                    error={errors.appId}
                  />
                </Card>
              )}

              <Card padding="lg" title="Protocol">
                <Grid columns={{ xs: 1, sm: 2 }} gap="md">
                  <Card
                    padding="lg"
                    variant={protocol === 'saml' ? 'outlined' : 'subtle'}
                    hoverable
                    onClick={() => setProtocol('saml')}
                    style={protocol === 'saml' ? {
                      borderColor: 'var(--brand-primary)',
                      borderWidth: '2px'
                    } : undefined}
                  >
                    <VStack gap="sm" style={{ alignItems: 'center', textAlign: 'center' }}>
                      <Badge size="lg">SAML 2.0</Badge>
                      <Text size="sm" color="muted">Azure AD, Okta, OneLogin</Text>
                    </VStack>
                  </Card>
                  <Card
                    padding="lg"
                    variant={protocol === 'oidc' ? 'outlined' : 'subtle'}
                    hoverable
                    onClick={() => setProtocol('oidc')}
                    style={protocol === 'oidc' ? {
                      borderColor: 'var(--brand-primary)',
                      borderWidth: '2px'
                    } : undefined}
                  >
                    <VStack gap="sm" style={{ alignItems: 'center', textAlign: 'center' }}>
                      <Badge size="lg">OpenID Connect</Badge>
                      <Text size="sm" color="muted">OAuth 2.0 / OIDC</Text>
                    </VStack>
                  </Card>
                </Grid>
              </Card>

              {protocol === 'saml' && (
                <Card padding="lg" title="SAML Configuration">
                  <VStack gap="md">
                    <Input
                      label="IdP Entity ID"
                      placeholder="https://login.microsoftonline.com/{tenant}/saml2"
                      value={samlIdpEntityId}
                      onChange={(v) => setSamlIdpEntityId(v as string)}
                      error={errors.samlIdpEntityId}
                    />
                    <Input
                      label="SSO URL"
                      placeholder="https://login.microsoftonline.com/{tenant}/saml2"
                      value={samlIdpSsoUrl}
                      onChange={(v) => setSamlIdpSsoUrl(v as string)}
                      error={errors.samlIdpSsoUrl}
                    />
                    <Textarea
                      label="X.509 Certificate (PEM)"
                      placeholder="-----BEGIN CERTIFICATE-----"
                      value={samlIdpCertificate}
                      onChange={(v) => setSamlIdpCertificate(v as string)}
                      rows={6}
                      error={errors.samlIdpCertificate}
                    />
                  </VStack>
                </Card>
              )}

              {protocol === 'oidc' && (
                <Card padding="lg" title="OIDC Configuration">
                  <VStack gap="md">
                    <Input
                      label="Issuer (optional)"
                      placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
                      value={oidcIssuer}
                      onChange={(v) => setOidcIssuer(v as string)}
                    />
                    <Input
                      label="Client ID"
                      value={oidcClientId}
                      onChange={(v) => setOidcClientId(v as string)}
                      error={errors.oidcClientId}
                    />
                    <HStack gap="sm" style={{ alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <Input
                          label="Client Secret"
                          type={showSecret ? 'text' : 'password'}
                          value={oidcClientSecret}
                          onChange={(v) => setOidcClientSecret(v as string)}
                          error={errors.oidcClientSecret}
                        />
                      </div>
                      <IconButton
                        icon={showSecret ? <EyeOff20Regular /> : <Eye20Regular />}
                        onClick={() => setShowSecret(!showSecret)}
                      />
                    </HStack>
                    <Input
                      label="Authorization URL"
                      value={oidcAuthorizationUrl}
                      onChange={(v) => setOidcAuthorizationUrl(v as string)}
                      error={errors.oidcAuthorizationUrl}
                    />
                    <Input
                      label="Token URL"
                      value={oidcTokenUrl}
                      onChange={(v) => setOidcTokenUrl(v as string)}
                      error={errors.oidcTokenUrl}
                    />
                    <Input
                      label="Userinfo URL (optional)"
                      value={oidcUserinfoUrl}
                      onChange={(v) => setOidcUserinfoUrl(v as string)}
                    />
                  </VStack>
                </Card>
              )}

              <Card padding="lg">
                <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <VStack gap="xs">
                    <Text weight="medium">Enable Connection</Text>
                    <Text size="sm" color="muted">
                      {isActive ? 'Users can sign in' : 'Connection disabled'}
                    </Text>
                  </VStack>
                  <Switch checked={isActive} onChange={setIsActive} />
                </HStack>
              </Card>
            </VStack>
          </TabPanel>

          <TabPanel id="options" active={activeTab}>
            <Card padding="lg" title="Provisioning Options">
              <VStack gap="lg">
                <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <VStack gap="xs">
                    <Text weight="medium">Just-In-Time Provisioning (JIT)</Text>
                    <Text size="sm" color="muted">Automatically create user account</Text>
                  </VStack>
                  <Switch checked={jitProvisioning} onChange={setJitProvisioning} />
                </HStack>

                <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <VStack gap="xs">
                    <Text weight="medium">Profile Update</Text>
                    <Text size="sm" color="muted">Sync profile on each login</Text>
                  </VStack>
                  <Switch checked={autoUpdateProfile} onChange={setAutoUpdateProfile} />
                </HStack>

                <Select
                  label="Default Role"
                  value={defaultRole}
                  onChange={(v) => setDefaultRole(v as string)}
                  options={[
                    { value: 'user', label: 'User' },
                    { value: 'member', label: 'Member' },
                    { value: 'admin', label: 'Administrator' }
                  ]}
                />
              </VStack>
            </Card>
          </TabPanel>

          {isEdit && (
            <TabPanel id="info" active={activeTab}>
              <Card padding="lg" title="Information to Configure in Your IdP">
                {protocol === 'saml' ? (
                  <VStack gap="md">
                    <CopyField label="SP Entity ID" value={spEntityId} />
                    <CopyField label="ACS URL" value={acsUrl} />
                    <div>
                      <Text size="sm" color="muted">Name ID Format</Text>
                      <Text weight="medium" size="sm">urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress</Text>
                    </div>
                  </VStack>
                ) : (
                  <VStack gap="md">
                    <CopyField label="Redirect URI" value={oidcRedirectUri} />
                    <div>
                      <Text size="sm" color="muted">Required Scopes</Text>
                      <HStack gap="xs" style={{ marginTop: '0.25rem' }}>
                        <Badge size="sm">openid</Badge>
                        <Badge size="sm">email</Badge>
                        <Badge size="sm">profile</Badge>
                      </HStack>
                    </div>
                  </VStack>
                )}
              </Card>
            </TabPanel>
          )}
        </TabPanels>
        </div>

        <HStack gap="md" style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
          <Button variant="secondary" onClick={() => navigate(`/org/${organization.slug}/sso`)}>
            Cancel
          </Button>
          <Button icon={<Save20Regular />} loading={saving} onClick={handleSubmit}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </HStack>
      </Animate>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Connection"
        description="Users will no longer be able to sign in via this SSO connection."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDelete}
      />
    </VStack>
  )
}
